import { Dispatch, SetStateAction } from "react";
import { useTranslate } from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { Checkbox } from "./Checkbox";
import { isPhoneValid } from "utils/isPhoneValid";

export type ReminderEnabledMap = Record<string, boolean>;

interface ReminderSettingsProps {
  enabledMap: ReminderEnabledMap;
  setEnabledMap: Dispatch<SetStateAction<ReminderEnabledMap>>;
  showReminders: boolean;
  remindersEditable: boolean;
  showPromo: boolean;
  promoEditable: boolean;
}

const ReminderSettings = ({
  enabledMap,
  setEnabledMap,
  showReminders,
  remindersEditable,
  showPromo,
  promoEditable
}: ReminderSettingsProps) => {
  const translate = useTranslate();
  const formContext = useFormContext();
  // Watch the live form values so the toggles react to what the admin is
  // currently entering, not just the value the record loaded with.
  const email = useWatch({ name: "email" }) as string | undefined;
  const phoneNumber = useWatch({ name: "phone_number" }) as string | undefined;

  if (!showReminders && !showPromo) {
    return null;
  }

  const hasEmail = !!email && email.trim().length > 0;
  const hasPhone = isPhoneValid(phoneNumber);

  const toggle = (type: string) => {
    setEnabledMap((prev) => ({ ...prev, [type]: !prev[type] }));
    // The toggles live in local state (outside react-hook-form), so mark the
    // form dirty to keep the Save button enabled, same as the card element.
    formContext?.setValue("fakeDirtyFormWorkaround", "true", {
      shouldDirty: true
    });
  };

  const buildLabel = (
    channel: "EMAIL" | "SMS",
    promo: boolean,
    hasContact: boolean
  ) => {
    if (!hasContact) {
      return channel === "EMAIL"
        ? translate("users.no_email")
        : translate("users.no_phone_number");
    }
    const value =
      channel === "EMAIL" ? (email as string) : (phoneNumber as string);
    return promo
      ? `${value} (${translate("users.promotional")})`
      : `${value} (${translate("users.contact")})`;
  };

  const rows: {
    type: string;
    channel: "EMAIL" | "SMS";
    promo: boolean;
    visible: boolean;
    roleEditable: boolean;
  }[] = [
    {
      type: "EMAIL",
      channel: "EMAIL",
      promo: false,
      visible: showReminders,
      roleEditable: remindersEditable
    },
    {
      type: "SMS",
      channel: "SMS",
      promo: false,
      visible: showReminders,
      roleEditable: remindersEditable
    },
    {
      type: "EMAIL_PROMO",
      channel: "EMAIL",
      promo: true,
      visible: showPromo,
      roleEditable: promoEditable
    },
    {
      type: "SMS_PROMO",
      channel: "SMS",
      promo: true,
      visible: showPromo,
      roleEditable: promoEditable
    }
  ];

  return (
    <div className="mb-4">
      <p className="text-[14px] font-medium font-['Inter'] mb-2">
        {translate("users.notification_settings_title")}
      </p>
      {rows
        .filter((row) => row.visible)
        .map((row) => {
          const hasContact = row.channel === "EMAIL" ? hasEmail : hasPhone;
          const editable = row.roleEditable && hasContact;
          return (
            <Checkbox
              key={row.type}
              id={row.type}
              cyId={`notification-setting-${row.type.toLowerCase()}`}
              value={!!enabledMap[row.type]}
              disabled={!editable}
              onChange={() => toggle(row.type)}
              label={buildLabel(row.channel, row.promo, hasContact)}
              className="mb-2"
            />
          );
        })}
    </div>
  );
};

export default ReminderSettings;
